import { useEffect, useRef, useState } from "react";
import { Pencil, Type, Undo2, Eraser, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Stroke = { kind: "path"; points: { x: number; y: number }[] } | { kind: "text"; x: number; y: number; text: string };
interface Props { open: boolean; imageUrl: string; onClose: () => void; onGenerate: (blob: Blob, notes: string[]) => Promise<void> }

export function AnnotateDialog({ open, imageUrl, onClose, onGenerate }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [tool, setTool] = useState<"pen" | "text">("pen");
  const [drawing, setDrawing] = useState(false);
  const [pendingText, setPendingText] = useState<{ x: number; y: number } | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => { imgRef.current = img; const c = canvasRef.current!; c.width = img.width; c.height = img.height; redraw([]); };
    img.src = imageUrl; setStrokes([]);
  }, [open, imageUrl]);

  const redraw = (list: Stroke[]) => {
    const c = canvasRef.current, img = imgRef.current; if (!c || !img) return;
    const ctx = c.getContext("2d")!; ctx.drawImage(img, 0, 0);
    const lw = Math.max(4, c.width / 220);
    ctx.strokeStyle = "#ff1f1f"; ctx.fillStyle = "#ff1f1f"; ctx.lineWidth = lw; ctx.lineCap = "round"; ctx.lineJoin = "round";
    list.forEach((s) => {
      if (s.kind === "path") { ctx.beginPath(); s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke(); }
      else { ctx.font = `bold ${Math.max(18, c.width / 32)}px Arial`; const w = ctx.measureText(s.text).width; ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.fillRect(s.x - 6, s.y - c.width / 32 - 4, w + 12, c.width / 32 + 12); ctx.fillStyle = "#ff1f1f"; ctx.fillText(s.text, s.x, s.y); }
    });
  };
  useEffect(() => redraw(strokes), [strokes]);

  const pos = (e: React.PointerEvent) => { const c = canvasRef.current!; const r = c.getBoundingClientRect(); return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height }; };
  const down = (e: React.PointerEvent) => { const p = pos(e); if (tool === "text") { setPendingText(p); return; } setDrawing(true); setStrokes((s) => [...s, { kind: "path", points: [p] }]); };
  const move = (e: React.PointerEvent) => { if (!drawing) return; const p = pos(e); setStrokes((s) => { const last = s[s.length - 1]; if (last?.kind !== "path") return s; return [...s.slice(0, -1), { ...last, points: [...last.points, p] }]; }); };
  const addLabel = () => { if (pendingText && label.trim()) setStrokes((s) => [...s, { kind: "text", ...pendingText, text: label.trim() }]); setPendingText(null); setLabel(""); };
  const notes = strokes.filter((s): s is Extract<Stroke, { kind: "text" }> => s.kind === "text").map((s) => s.text);

  const generate = async () => {
    setBusy(true);
    try { const blob: Blob = await new Promise((res) => canvasRef.current!.toBlob((b) => res(b!), "image/jpeg", 0.9)); await onGenerate(blob, notes); onClose(); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="annotate-dialog">
        <DialogHeader><DialogTitle>Annotate before regenerating</DialogTitle><DialogDescription>Draw in red and add short labels like “skip this gable” or “wrap this tree”. The AI follows the notes and never paints the markings.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant={tool === "pen" ? "default" : "outline"} className={tool === "pen" ? "bg-blue-700" : ""} data-testid="annotate-tool-pen" onClick={() => setTool("pen")}><Pencil size={13} className="mr-1" />Draw</Button>
          <Button size="sm" variant={tool === "text" ? "default" : "outline"} className={tool === "text" ? "bg-blue-700" : ""} data-testid="annotate-tool-text" onClick={() => setTool("text")}><Type size={13} className="mr-1" />Label</Button>
          <Button size="sm" variant="ghost" data-testid="annotate-undo" onClick={() => setStrokes((s) => s.slice(0, -1))}><Undo2 size={13} className="mr-1" />Undo</Button>
          <Button size="sm" variant="ghost" data-testid="annotate-clear" onClick={() => setStrokes([])}><Eraser size={13} className="mr-1" />Clear</Button>
          <span className="ml-auto text-xs text-gray-500" data-testid="annotate-count">{strokes.length} marks · {notes.length} labels</span>
        </div>
        <div className="relative overflow-hidden rounded-md bg-black">
          <canvas ref={canvasRef} data-testid="annotate-canvas" className={`block max-h-[60vh] w-full touch-none object-contain ${tool === "pen" ? "cursor-crosshair" : "cursor-text"}`}
            onPointerDown={down} onPointerMove={move} onPointerUp={() => setDrawing(false)} onPointerLeave={() => setDrawing(false)} />
          {pendingText && (
            <div className="absolute inset-x-0 bottom-0 flex gap-2 bg-white/95 p-2" data-testid="annotate-label-form">
              <Input autoFocus data-testid="annotate-label-input" placeholder="e.g. skip this gable" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLabel()} className="h-8 text-sm" />
              <Button size="sm" data-testid="annotate-label-add" onClick={addLabel} className="bg-blue-700 hover:bg-blue-800">Add label</Button>
              <Button size="sm" variant="ghost" onClick={() => setPendingText(null)}>Cancel</Button>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="annotate-cancel-btn">Cancel</Button>
          <Button data-testid="annotate-generate-btn" disabled={busy || strokes.length === 0} onClick={generate} className="bg-amber-600 hover:bg-amber-700"><Sparkles size={13} className="mr-1.5" />{busy ? "Generating…" : "Generate with these notes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
